import { TextField, CircularProgress, Popper } from '@mui/material';
import { Autocomplete } from '@mui/material';
import React, { memo, useContext, useState } from 'react';
import __t from '../../lib/traduzioni';
import { Controller } from 'react-hook-form';
import {useAsyncFn, useDebounce} from 'react-use'
import DefaultLoadingComponent from './DefaultLoadingComponent';
import useBSSController from '../../hooks/useBSSController';
import useInitialLoader from '../../hooks/useInitialLoader'; 
import useBSSLogger from '../../hooks/useBSSLogger';
import memoFieldComparator from '../../lib/memoFieldComparator';
import BSSDataContext from '../../contexts/BSSDataContext';

const {logger} = useBSSLogger();

/**
 * Il parametro remoteSearch è un oggetto con all'interno una funzione, i parametri che prende e il debounce con cui viene richiamata.
 * I parametri che vengono passati a remoteSearch.fn sono [...params, ...dipendenze, term]
 * 
 * Dipendenze sono i valori degli altri campi da cui dipende.
 * 
 * Es. applicato al campo CercaComune: {fn: retrieveComuni, dipendenze: {nazione: {id...}}}
 *
 * 
 * @param {{remoteSearch: {fn: Promise, params: any[] = [], debounce?: Number = 500}, dipendenze: String[]}} param0 
 * @returns 
 */
const useRemoteSearch = ({remoteSearch, dipendenze}) => {
    const [open, setOpen] = useState(false);
    const [term, setTerm] = useState("");

    
    const asyncFn = async (params) => {
        try {
            logger.debug("eseguo remote search passando parametri", params, params[params.length - 1])
            const res = await remoteSearch.fn(...params);
            return res; 
        } catch(err){
            logger.error("problema remote search", err);
        }
        
    };

    const [{loading, value: options = []}, callback] = useAsyncFn(asyncFn)

    const [, cancel] = useDebounce(
        () => {
            if(![null, undefined].includes(term) && ((term?.length ?? 0) > 0) /*&& [null, undefined].includes(value)*/){
                logger.debug("termine di ricerca cambiato lancio la ricerca per", term)
                callback([...remoteSearch.params, ...dipendenze, term])
            }
        },
        remoteSearch.debounceTime || 500,         
        [callback, term]
    )
    const opencloseprops = {open, onOpen: () => setOpen(true), onClose: () => setOpen(false)};
    return {
        open,
        setOpen,
        term, 
        setTerm,
        loading, 
        options,
        opencloseprops
    }
}

const CommonDefaultAutocompleteProps = ({disabilita, options, value, fieldState, label, ref, classes, loading, TextFieldInputProps, TextFieldProps}) => { 

    return{
        autoComplete: true,   
        disabled: disabilita,
        options: options,    
        autoHighlight: true,
        value: value,
        isOptionEqualToValue: (option, value) => option.id === (value?.id ?? null),                                    
        getOptionLabel: (option) => option.label,
        renderOption: (props, option, { selected }) => (
            <li {...props}>
                <div>{option.label}</div>
            </li>
        ),
        
        //senza questo il dropdown risulterebbe staccato dall'input
        PopperComponent: (props) => {
            return (
                <Popper {...props} container={props.anchorEl} />
            )
        },
        renderInput: (params) => (
            <TextField 
                error={fieldState?.invalid ?? false}
                {...params}
                variant="outlined" 
                size="small"                                         
                fullWidth 
                label={__t(label)}
                helperText={fieldState?.error?.message ?? undefined}
                inputRef={ref}
                InputProps={{
                    ...params.InputProps,    
                    classes: {root: classes.hasRightGroup},
                    autoComplete: 'off',
                    endAdornment: (
                        <>
                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                        </>
                    ),
                    ...TextFieldInputProps
                }}
                {...TextFieldProps}
            />
        )
    }                                   
}

/**
 * Autocomplete di Material UI (mui) integrata in React Hook Form (rhf)
 * Accetta come dataProvider una promise che ritorna un Array o un Array di valori
 * Permette di personalizzare/sovrascrivere con le props previste dalle rispettive librerie (tra parentesi):
 * 1. Controller (rhf)
 * 2. Autocomplete (mui)
 * 3. TextInput di ricerca (mui)
 * 
 * @param {BssAutocompleteProps} param0 
 * @returns 
 */
const BSSAutocomplete = ({
    name, 
    label = name,
    methods,
    dataProvider = [], 
    dependsOn = null,
    LoadingComponent = DefaultLoadingComponent,
    remoteSearch = null,
    ControllerProps = {},
    AutocompleteProps = {},
    TextFieldProps = {},
    TextFieldInputProps = {},
}) => {

    const {dipendenze = [], disabilitaByDep: disabilita, classes, defaultValue} = useBSSController({name, dependsOn, defaultValue: options?.[0] ?? null, methods})

    if(remoteSearch === null){
        var {loading, error, data: options} =  useInitialLoader({name, dataProvider})
    } else {
        var {loading, open, term, setTerm, opencloseprops, options} = useRemoteSearch({remoteSearch, dipendenze})
    }

    const acpropsremotesearch = remoteSearch === null ? {} : {
        ...opencloseprops,
        loading: open && loading,
        onInputChange: (e, newval) => setTerm(newval),
        filterOptions: x => x
    }

    const acprops = ({value, onChange, ref, fieldState}) => ({
        ...CommonDefaultAutocompleteProps({disabilita, options, value, fieldState, label, ref, classes, loading, TextFieldInputProps, TextFieldProps}),
        noOptionsText: remoteSearch === null ? 
            __t("nooptions") :  
            !term.length ? 
                __t("startwritingtosearch", [label]) : 
                __t("nooptionsforterm", [term]),
        onChange: (_, data) => {
            if(AutocompleteProps.onChange){
                AutocompleteProps.onChange(data)
            } else {
                logger.debug("eseguo l'onchange standard di autocomplete")
                if(remoteSearch !== null){
                    setTerm("");
                }
                onChange(data);
            }
            
            if(AutocompleteProps.afterChange){
                AutocompleteProps.afterChange(data)
            }
        },
        ...acpropsremotesearch,        
        ...AutocompleteProps
        
    })    

    return (
        <>
            {remoteSearch === null && loading && <LoadingComponent />}
            {((remoteSearch === null && !error && !loading && options?.length) || (remoteSearch !== null)) && (
                <>
                    <Controller
                        {...{name, control: methods.control, options, defaultValue}}                                                                 
                        render={({field: {value = null, onChange, ref}, fieldState}) => (
                            <>                            
                                <Autocomplete {...acprops({value, onChange, ref, fieldState})} />
                            </>
                        )}
                        {...ControllerProps}
                    />
                </>
            )}
        </>
    )
}

export default BSSAutocomplete